
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, setDoc, doc, getDocs } from 'firebase/firestore';
import { UserRole } from '../types';

const MarksManagement: React.FC<{ user: any }> = ({ user }) => {
  const [students, setStudents] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [scores, setScores] = useState<any[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    // 1. Fetch relevant subjects
    const subQuery = user.role === UserRole.STAFF 
      ? query(collection(db, 'subjects'), where('staffId', '==', user.uid))
      : collection(db, 'subjects');
    
    const unsubscribeSubs = onSnapshot(subQuery, (snap) => {
      setSubjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // 2. Fetch students for the table (Staff/Admin only)
    let unsubscribeUsers = () => {};
    if (user.role !== UserRole.STUDENT) {
      const userQuery = query(collection(db, 'users'), where('role', '==', UserRole.STUDENT));
      unsubscribeUsers = onSnapshot(userQuery, (snap) => {
        setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    }

    // 3. Fetch scores
    const scoreQuery = user.role === UserRole.STUDENT 
      ? query(collection(db, 'marks'), where('studentId', '==', user.uid))
      : collection(db, 'marks');
    
    const unsubscribeScores = onSnapshot(scoreQuery, (snap) => {
      setScores(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubscribeSubs();
      unsubscribeUsers();
      unsubscribeScores();
    };
  }, [user]);

  const updateMark = async (studentId: string, markStr: string) => {
    if (!selectedSubject) return;
    const mark = parseInt(markStr);
    if (isNaN(mark) || mark < 0 || mark > 100) return;

    const id = `${studentId}_${selectedSubject}`;
    setSavingId(id);
    
    try {
      await setDoc(doc(db, 'marks', id), {
        studentId,
        subjectId: selectedSubject,
        score: mark,
        updatedAt: Date.now(),
        updatedBy: user.displayName
      });
      // Clear saving indicator after a delay
      setTimeout(() => setSavingId(null), 1500);
    } catch (err) {
      console.error(err);
      alert("Failed to save mark");
      setSavingId(null);
    }
  };

  const getScoreValue = (studentId: string) => {
    const scoreObj = scores.find(s => s.studentId === studentId && s.subjectId === selectedSubject);
    return scoreObj ? scoreObj.score : '';
  };

  if (user.role === UserRole.STUDENT) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg">
             <i className="fas fa-award text-xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-black dark:text-white">Examination Results</h2>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Academic Session 2026-2027</p>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {scores.map(s => {
            const subject = subjects.find(sub => sub.id === s.subjectId);
            return (
              <div key={s.id} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative p-8 bg-white dark:bg-dark-card rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm transition-transform hover:-translate-y-1">
                  <div className="flex justify-between items-start mb-6">
                    <span className="px-3 py-1 bg-gray-50 dark:bg-gray-800 text-[10px] font-black text-gray-400 rounded-lg uppercase tracking-tighter">
                      {subject?.name || 'Subject'}
                    </span>
                    <i className="fas fa-medal text-purple-200 dark:text-gray-700 text-2xl"></i>
                  </div>
                  <div className="flex items-end gap-2">
                    <h3 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">{s.score}</h3>
                    <span className="text-lg font-bold text-gray-300 mb-1.5">%</span>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-50 dark:border-gray-800 flex justify-between items-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Status</p>
                    <p className="text-[10px] font-black text-green-500 uppercase">Distinction</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {scores.length === 0 && (
          <div className="text-center py-24 bg-white dark:bg-dark-card rounded-[3rem] border-2 border-dashed border-gray-100 dark:border-gray-800">
            <i className="fas fa-receipt text-5xl text-gray-100 mb-6"></i>
            <h3 className="text-xl font-bold dark:text-white">No Results Published</h3>
            <p className="text-gray-400 text-sm mt-2">Results for the current session will appear here once released.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold dark:text-white">Assign Exam Scores</h2>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <label className="text-xs font-black text-gray-400 uppercase tracking-widest hidden sm:block">Select Subject:</label>
          <select 
            className="flex-1 md:flex-none min-w-[200px] bg-white dark:bg-dark-card border-2 border-gray-100 dark:border-gray-800 p-3 rounded-2xl dark:text-white font-bold outline-none focus:border-purple-500 transition-all shadow-sm" 
            value={selectedSubject} 
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">-- Choose Subject --</option>
            {subjects.map(s => <option key={s.id} value={s.id}>{s.name} ({s.classId})</option>)}
          </select>
        </div>
      </div>

      {!selectedSubject ? (
        <div className="p-20 text-center bg-gray-50/50 dark:bg-gray-800/10 rounded-[3rem] border-2 border-dashed border-gray-100 dark:border-gray-800">
           <i className="fas fa-mouse-pointer text-4xl text-gray-200 mb-4"></i>
           <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">Select a subject to begin grading</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-card rounded-[2.5rem] overflow-hidden border border-gray-100 dark:border-gray-800 shadow-xl animate-fadeIn">
          <div className="table-container">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Student Information</th>
                  <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Grade (0-100)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {students.map(s => {
                  const currentSaving = savingId === `${s.id}_${selectedSubject}`;
                  return (
                    <tr key={s.id} className="hover:bg-gray-50/30 dark:hover:bg-gray-800/20 transition-colors">
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                          <img src={s.photoURL} className="w-10 h-10 rounded-xl object-cover" alt="" />
                          <div>
                            <p className="font-black dark:text-white text-sm">{s.displayName}</p>
                            <p className="text-[10px] text-gray-400 uppercase font-bold">{s.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <div className="inline-flex items-center gap-3">
                          {currentSaving && (
                            <span className="text-[10px] font-black text-green-500 uppercase animate-pulse">
                              <i className="fas fa-check-circle mr-1"></i> Saved
                            </span>
                          )}
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            defaultValue={getScoreValue(s.id)}
                            placeholder="0" 
                            className="w-24 bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-purple-500 p-3 rounded-xl text-center dark:text-white font-black shadow-inner outline-none transition-all"
                            onBlur={(e) => updateMark(s.id, e.target.value)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarksManagement;
